import { SegmentedMessage } from 'sms-segments-calculator';

export type SmsEncodingName = 'GSM-7' | 'UCS-2';

export interface SmsCounter {
  segments: number;
  remaining: number; // chars remaining in the current segment, based on encoding units
  charactersCount: number;
  encodingName: SmsEncodingName;
}

function normalizeCrs(text: string): string {
  // Normalize CRLF -> CR for consistent SMS calculations
  return text.replace(/\r\n/g, '\r');
}

/**
 * Returns segments and remaining chars leveraging the library:
 * - segments: SegmentedMessage.segmentsCount
 * - remaining: floor(lastSegment.freeSizeInBits() / unitBits)
 *   unitBits = 7 for GSM-7, 16 for UCS-2
 * Concatenation UDH is already accounted for in freeSizeInBits().
 */
export function getSmsSegmentsAndRemaining(
  message: string,
  opts?: { enforceEncoding?: SmsEncodingName | 'auto'; smartEncoding?: boolean },
): SmsCounter {
  const normalized = normalizeCrs(message);
  const sm = new SegmentedMessage(normalized, opts?.enforceEncoding ?? 'auto', !!opts?.smartEncoding);

  const segments = Number(sm.segmentsCount ?? 1);
  const encodingName = sm.encodingName as SmsEncodingName;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const unitBits = encodingName === 'GSM-7' ? 7 : 16;

  const lastSegment = sm.segments?.[sm.segments.length - 1] as { freeSizeInBits: () => number } | undefined;
  const freeBits = typeof lastSegment?.freeSizeInBits === 'function' ? lastSegment.freeSizeInBits() : 0;

  const remaining = Math.max(0, Math.floor(freeBits / unitBits));
  return {
    segments,
    remaining,
    charactersCount: Number(sm.numberOfCharacters ?? 0),
    encodingName,
  };
}
